import { Link } from "react-router-dom";
import { Page, Reveal, MaskedLine, Eyebrow } from "@/components/motion/Reveal";
import { IMG } from "@/constants/images";

const AUDIENCE = ["Professionals", "Executives", "Entrepreneurs", "Emerging Leaders", "Individuals in Transition"];

const FOCUS_AREAS = [
  "Leadership Development",
  "Professional Clarity",
  "Purpose",
  "Career Transitions",
  "Executive Presence",
  "Confidence",
  "Decision-Making",
  "Personal Leadership",
  "Entrepreneurial Leadership",
  "Next-Chapter Planning",
];

const METHOD = [
  { n: "01", title: "Assessment", desc: "An honest look at where you are: strengths, gaps, and the season you're in." },
  { n: "02", title: "Blueprint", desc: "A clear, personal development plan for where you're going and who you're becoming." },
  { n: "03", title: "Execution", desc: "Structured sessions, accountability, and deliberate practice that create momentum." },
  { n: "04", title: "Mastery", desc: "Leadership that holds, in transition, under pressure, and at the next level." },
];

export default function Coaching() {
  return (
    <Page data-testid="coaching-page">
      {/* HERO */}
      <section data-testid="coaching-hero" className="bg-obsidian text-ivory pt-40 pb-24 lg:pb-32 relative overflow-hidden">
        <div className="absolute inset-0 hero-glow opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 relative">
          <Eyebrow>Executive Coaching · For the Person</Eyebrow>
          <h1 className="font-serif font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-8 leading-[1.1] max-w-4xl">
            <MaskedLine delay={0.2}>Your Next Level</MaskedLine>
            <MaskedLine delay={0.38} className="italic text-gold">Begins with Clarity.</MaskedLine>
          </h1>
          <Reveal delay={0.55}>
            <p className="text-base sm:text-lg font-light leading-relaxed text-ivory/55 mt-8 max-w-2xl">
              One-on-one coaching for professionals, executives, entrepreneurs,
              emerging leaders, and individuals navigating growth or transition.
            </p>
          </Reveal>
          <Reveal delay={0.65}>
            <div className="flex flex-wrap gap-2 mt-10" data-testid="coaching-audience-chips">
              {AUDIENCE.map((a) => (
                <span key={a} className="px-4 py-2 border border-white/15 font-mono text-[10px] tracking-[0.2em] uppercase text-ivory/60">
                  {a}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.75}>
            <Link
              to="/consultation"
              data-testid="coaching-hero-cta"
              className="inline-block mt-12 bg-gold text-obsidian px-10 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-gold-dark transition-colors duration-300"
            >
              Book a Consultation
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FOCUS AREAS */}
      <section data-testid="coaching-focus-section" className="bg-ivory py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Reveal><Eyebrow>Focus Areas</Eyebrow></Reveal>
              <Reveal delay={0.1}>
                <h2 className="font-serif text-3xl sm:text-4xl text-obsidian mt-8 leading-tight">
                  The work we do <span className="italic text-gold-dark">together.</span>
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="text-base font-light text-stone-text leading-relaxed mt-6">
                  Coaching at Genesis is personal, structured, and honest. Every
                  engagement is built around the leader.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-obsidian/10 border border-obsidian/10 self-start">
              {FOCUS_AREAS.map((f, i) => (
                <Reveal key={f} delay={i * 0.04} className="bg-ivory">
                  <div className="p-8 h-full hover:bg-cream transition-colors duration-500" data-testid={`focus-area-${i}`}>
                    <span className="font-mono text-[10px] text-gold tracking-[0.3em]">{String(i + 1).padStart(2, "0")}</span>
                    <p className="font-serif text-lg text-obsidian mt-3 leading-snug">{f}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* METHOD */}
      <section data-testid="coaching-method-section" className="bg-cream py-24 lg:py-32 border-y border-obsidian/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><Eyebrow>The Coaching Journey</Eyebrow></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 mt-12">
            {METHOD.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.1} className={`py-10 lg:px-8 ${i > 0 ? "lg:border-l border-obsidian/10" : "lg:pl-0"} border-t sm:border-t-0`}>
                <p className="font-mono text-xs text-gold tracking-[0.3em]">{m.n}</p>
                <h3 className="font-serif text-2xl text-obsidian mt-4">{m.title}</h3>
                <p className="text-sm font-light text-stone-text mt-3 leading-relaxed">{m.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* IMAGE + PROMISE */}
      <section className="bg-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-16">
          <Reveal>
            <div className="relative overflow-hidden">
              <img src={IMG.architectureWhite} alt="Architectural detail, clarity and structure" data-testid="coaching-editorial-image" className="w-full h-64 sm:h-96 object-cover" />
              <div className="absolute inset-0 bg-obsidian/25" />
              <p className="absolute bottom-6 left-6 font-mono text-[10px] tracking-[0.3em] uppercase text-ivory/80">
                Inspiration + Clarity + Strategy + Action
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="coaching-promise-section" className="bg-obsidian text-ivory py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 text-center">
          <Reveal>
            <p className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-snug">
              Develop the leader required <span className="italic text-gold">for what's next.</span>
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/consultation"
              data-testid="coaching-cta-book-consultation"
              className="inline-block mt-12 bg-gold text-obsidian px-10 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-gold-dark transition-colors duration-300"
            >
              Book a Consultation
            </Link>
          </Reveal>
        </div>
      </section>
    </Page>
  );
}
